import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

type ResizablePanelProps = {
  children: ReactNode;
  direction: 'horizontal' | 'vertical';
  defaultSize: number;
  /** Controlled size - when provided, overrides internal state */
  size?: number;
  minSize?: number;
  maxSize?: number;
  resizerPosition: 'start' | 'end';
};

const useStyles = makeStyles({
  container: {
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1002, // Above NodeEditorModal to remain visible when editor opens
  },
  horizontalResizer: {
    position: 'absolute',
    top: 0,
    width: '6px',
    height: '100%',
    backgroundColor: 'transparent',
    cursor: 'col-resize',
    // Must be above nested ResizablePanel containers (which have
    // zIndex: 1002 to stay above NodeEditorModal). Without this, when a
    // vertical ResizablePanel wraps another horizontal ResizablePanel
    // (e.g. the bottom panel's height resizer above the chat-log/logs
    // split), the inner panel's zIndex: 1002 covers the outer resizer
    // and the user can't drag the top edge above the inner panel area.
    zIndex: 1003,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  horizontalResizerStart: {
    left: 0,
  },
  horizontalResizerEnd: {
    right: 0,
  },
  verticalResizer: {
    position: 'absolute',
    left: 0,
    height: '6px',
    width: '100%',
    backgroundColor: 'transparent',
    cursor: 'row-resize',
    // See note on horizontalResizer — kept in sync so a vertical
    // resizer wrapping a horizontal one (or vice versa) is still
    // reachable by the cursor over the inner panel area.
    zIndex: 1003,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  verticalResizerStart: {
    top: 0,
  },
  verticalResizerEnd: {
    bottom: 0,
  },
  resizerActive: {
    backgroundColor: tokens.colorBrandBackground,
  },
  content: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
});

export const ResizablePanel = ({
  children,
  direction,
  defaultSize,
  size: controlledSize,
  minSize = 100,
  maxSize = 800,
  resizerPosition,
}: ResizablePanelProps) => {
  const styles = useStyles();
  const [internalSize, setInternalSize] = useState(defaultSize);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef(0);
  const startSizeRef = useRef(0);

  // Use controlled size if provided, otherwise use internal state
  const size = controlledSize ?? internalSize;
  const setSize = setInternalSize;

  // Sync internal size when controlled size changes, or reset to default when uncontrolled
  useEffect(() => {
    if (controlledSize !== undefined) {
      setInternalSize(controlledSize);
    } else {
      // Reset to default size when controlled size is removed
      setInternalSize(defaultSize);
    }
  }, [controlledSize, defaultSize]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      startPosRef.current = direction === 'horizontal' ? e.clientX : e.clientY;
      startSizeRef.current = size;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const currentPos = direction === 'horizontal' ? moveEvent.clientX : moveEvent.clientY;
        const delta = currentPos - startPosRef.current;

        let newSize: number;
        if (direction === 'horizontal') {
          newSize = resizerPosition === 'start'
            ? startSizeRef.current - delta
            : startSizeRef.current + delta;
        } else {
          newSize = resizerPosition === 'start'
            ? startSizeRef.current - delta
            : startSizeRef.current + delta;
        }

        const clampedSize = Math.max(minSize, Math.min(maxSize, newSize));
        setSize(clampedSize);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [direction, resizerPosition, size, minSize, maxSize, setSize]
  );

  // Keyboard resize for accessibility. Arrows nudge by SMALL_STEP px,
  // Shift+arrows by LARGE_STEP px, Home/End snap to min/max.
  // Direction of growth honors resizerPosition the same way mouse drag does.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const SMALL_STEP = 16;
      const LARGE_STEP = 40;
      const isHorizontal = direction === 'horizontal';
      const growKey = isHorizontal
        ? (resizerPosition === 'start' ? 'ArrowLeft' : 'ArrowRight')
        : (resizerPosition === 'start' ? 'ArrowUp' : 'ArrowDown');
      const shrinkKey = isHorizontal
        ? (resizerPosition === 'start' ? 'ArrowRight' : 'ArrowLeft')
        : (resizerPosition === 'start' ? 'ArrowDown' : 'ArrowUp');

      let next: number | null = null;
      const step = e.shiftKey ? LARGE_STEP : SMALL_STEP;
      if (e.key === growKey) next = size + step;
      else if (e.key === shrinkKey) next = size - step;
      else if (e.key === 'Home') next = minSize;
      else if (e.key === 'End') next = maxSize;
      else return;

      e.preventDefault();
      setSize(Math.max(minSize, Math.min(maxSize, next)));
    },
    [direction, resizerPosition, size, minSize, maxSize, setSize]
  );

  const isHorizontal = direction === 'horizontal';
  const resizerClass = isHorizontal
    ? mergeClasses(styles.horizontalResizer, resizerPosition === 'start' ? styles.horizontalResizerStart : styles.horizontalResizerEnd)
    : mergeClasses(styles.verticalResizer, resizerPosition === 'start' ? styles.verticalResizerStart : styles.verticalResizerEnd);

  const containerStyle = isHorizontal
    ? { width: size, height: '100%' }
    : { height: size, width: '100%' };

  return (
    <div
      ref={containerRef}
      className={styles.container}
      style={containerStyle}
    >
      <div
        className={mergeClasses(resizerClass, isDragging && styles.resizerActive)}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        role="separator"
        aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
        aria-label={`Resize ${direction === 'horizontal' ? 'width' : 'height'}`}
        aria-valuemin={minSize}
        aria-valuemax={maxSize}
        aria-valuenow={size}
        tabIndex={0}
      />
      <div className={styles.content}>{children}</div>
    </div>
  );
};
